import { Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { NnrOrderLocalStore } from '@planning/rt-stores/nnrOrder/NnrOrderLocalStore'
import { PaginatedLocalStore } from '@planning/stores/PaginatedLocalStore'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ReleaseExpiredAssignmentAlert } from '../../Components/atoms/ReleaseExpiredAssignmentAlert'
import { NonNumericReleaseDetailsHeader } from '../../Components/organisms/NonNumericReleases/NonNumericReleaseDetailsHeader'
import { NonNumericReleaseDetailsTable } from '../../Components/organisms/NonNumericReleases/NonNumericReleaseDetailsTable'
import { NonNumericReleaseSummary } from '../../Components/organisms/NonNumericReleases/NonNumericReleaseSummary'
import { NonNumericReleaseDetailsViewStore } from '../../Stores/NonNumericReleaseDetailsViewStore'

export const NonNumericReleasesDetailsPage = observer(() => {
  const navigate = useNavigate()
  const theme = useMinimalsTheme()

  const {
    nnrOrderItemStore,
    orderItemStore,
    containerItemStore,
    containerJourneyItemStore,
    vesselVisitItemStore,
    railVisitItemStore,
    truckVisitItemStore,
    vesselItemStore,
    truckItemStore,
    companyItemStore,
  } = usePlanningStore()

  const { nnrOrderId } = useParams()

  if (!Number(nnrOrderId) || Number(nnrOrderId) < 0) {
    navigate(PATHS.orders.nonNumericReleases.url)
  }

  const id = Number(nnrOrderId)

  const nnrOrderLocalStore = useMemo(
    () =>
      new NnrOrderLocalStore(
        nnrOrderItemStore,
        orderItemStore,
        containerItemStore,
        containerJourneyItemStore,
        vesselVisitItemStore,
        railVisitItemStore,
        truckVisitItemStore,
        vesselItemStore,
        truckItemStore,
        id,
      ),
    [
      nnrOrderItemStore,
      orderItemStore,
      containerItemStore,
      containerJourneyItemStore,
      vesselVisitItemStore,
      railVisitItemStore,
      truckVisitItemStore,
      vesselItemStore,
      truckItemStore,
      id,
    ],
  )

  const orderLocalPaginationStore = useMemo(
    () => new PaginatedLocalStore(nnrOrderLocalStore),
    [nnrOrderLocalStore],
  )

  const releaseDetailsViewStore = useMemo(
    () =>
      new NonNumericReleaseDetailsViewStore(
        id,
        nnrOrderItemStore,
        railVisitItemStore,
        vesselVisitItemStore,
        companyItemStore,
      ),
    [companyItemStore, id, nnrOrderItemStore, railVisitItemStore, vesselVisitItemStore],
  )

  const { loading } = useAsyncFetch(async () => {
    await Promise.all([
      nnrOrderLocalStore.fetch(),
      releaseDetailsViewStore.fetchVisits(),
      releaseDetailsViewStore.fetchTransportCompany(),
    ])
  }, [nnrOrderLocalStore, releaseDetailsViewStore])

  if (!nnrOrderLocalStore.nnrOrder) return <></>

  const nnrOrder = nnrOrderLocalStore.nnrOrder

  return (
    <InitializationWrapper isInitializing={loading}>
      <Box
        sx={{
          height: '100%',
          bgcolor: theme.palette.grey[100],
        }}
      >
        <NonNumericReleaseDetailsHeader viewStore={releaseDetailsViewStore} nnrOrder={nnrOrder} />
        {nnrOrder.isExpired && (
          <Box padding={theme.customSpacing.m} pb={0}>
            <ReleaseExpiredAssignmentAlert />
          </Box>
        )}
        <Box padding={theme.customSpacing.m}>
          <NonNumericReleaseSummary nonNumericReleaseItem={nnrOrder} />
          <NonNumericReleaseDetailsTable
            viewStore={releaseDetailsViewStore}
            nnrOrder={nnrOrder}
            orderStore={orderLocalPaginationStore}
          />
        </Box>
      </Box>
    </InitializationWrapper>
  )
})
